import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

import { RegistryOrderError } from '@/services/registry-order-error';
import type {
  RegistryOrderBatchSubmit,
  RegistryOrderCreateRequest,
  RegistryOrderDetail,
  RegistryOrderOutcome,
} from '@/types/registry-documents';
import { toError } from '@/utils/errors';

/** Where one company's order stands while the batch is sent. */
export type BatchRow =
  | { status: 'pending' }
  | { status: 'sending' }
  | { status: 'done'; order: RegistryOrderDetail }
  /** An open order for the same documents is already on the report; the
   * row can be sent again as a deliberate re-order. */
  | { status: 'duplicate'; error: RegistryOrderError }
  | { status: 'failed'; error: Error };

interface UseBatchSubmitOptions {
  opened: boolean;
  onSubmitMany?: RegistryOrderBatchSubmit;
  onOrdered?: (order: RegistryOrderDetail) => void;
}

const UNREPORTED = 'No outcome was reported for this order - check the report';

function rowFor(outcome: RegistryOrderOutcome): BatchRow {
  if (outcome.ok) {
    return { status: 'done', order: outcome.order };
  }
  const { error } = outcome;
  if (error instanceof RegistryOrderError && error.kind === 'duplicate') {
    return { status: 'duplicate', error };
  }
  return { status: 'failed', error };
}

function unsettled(row: BatchRow): boolean {
  return row.status === 'pending' || row.status === 'sending';
}

/** The rows with `index` settled and `next`, when there is one, in flight. */
function withOutcome(rows: BatchRow[], index: number, row: BatchRow, next: number | null) {
  return rows.map((entry, i) => {
    if (i === index) {
      return row;
    }
    return i === next ? { status: 'sending' as const } : entry;
  });
}

function failUnsettled(rows: BatchRow[], error: Error): BatchRow[] {
  return rows.map((row) => (unsettled(row) ? { status: 'failed', error } : row));
}

/** One open session of the modal: what a running batch may still touch. */
interface BatchSession {
  isCurrent: () => boolean;
  setRows: Dispatch<SetStateAction<BatchRow[]>>;
  setInFlight: (inFlight: boolean) => void;
  onOrdered?: (order: RegistryOrderDetail) => void;
}

/** Send `batch` through `send`, settling rows as outcomes land; `rowOf`
 * maps the batch's positions to checklist rows. Nothing is touched once
 * the session is over. */
async function runBatch(
  session: BatchSession,
  send: RegistryOrderBatchSubmit,
  batch: RegistryOrderCreateRequest[],
  rowOf: (i: number) => number
): Promise<void> {
  const update = (change: (rows: BatchRow[]) => BatchRow[]) => {
    if (session.isCurrent()) {
      session.setRows(change);
    }
  };
  const report = (i: number, outcome: RegistryOrderOutcome) => {
    if (!session.isCurrent()) {
      return;
    }
    // The next company goes the moment this one settles.
    const next = i + 1 < batch.length ? rowOf(i + 1) : null;
    session.setRows((rows) => withOutcome(rows, rowOf(i), rowFor(outcome), next));
    if (outcome.ok) {
      session.onOrdered?.(outcome.order);
    }
  };
  session.setInFlight(true);
  try {
    await send(batch, report);
    // A handler that settled without reporting a row left it unsent.
    update((rows) => failUnsettled(rows, new Error(UNREPORTED)));
  } catch (err) {
    // The batch itself failed, not one order: whatever was not reported
    // settled is marked so, rather than left spinning.
    update((rows) => failUnsettled(rows, toError(err, 'Could not place the orders')));
  } finally {
    if (session.isCurrent()) {
      session.setInFlight(false);
    }
  }
}

/**
 * Sending one order per company, and where each one stands.
 *
 * The parent places the orders one after another and reports each outcome
 * as it lands; a failure on one company never stops the next. Like a
 * single order, a batch can outlive the modal: each close starts a new
 * session, and outcomes from an earlier session are dropped - the orders
 * themselves still complete server side.
 */
export function useBatchSubmit({ opened, onSubmitMany, onOrdered }: UseBatchSubmitOptions) {
  const [rows, setRows] = useState<BatchRow[]>([]);
  const [inFlight, setInFlight] = useState(false);
  const sessionRef = useRef(0);
  const payloadsRef = useRef<RegistryOrderCreateRequest[]>([]);

  useEffect(() => {
    if (!opened) {
      sessionRef.current += 1;
      payloadsRef.current = [];
      setRows([]);
      setInFlight(false);
    }
  }, [opened]);

  const sessionNow = (): BatchSession => {
    const session = sessionRef.current;
    return { isCurrent: () => sessionRef.current === session, setRows, setInFlight, onOrdered };
  };

  /** Send every payload, in order. Nothing happens without a handler. */
  const start = (payloads: RegistryOrderCreateRequest[]) => {
    if (!onSubmitMany || payloads.length === 0) {
      return Promise.resolve();
    }
    payloadsRef.current = payloads;
    setRows(payloads.map((_, i) => ({ status: i === 0 ? 'sending' : 'pending' })));
    return runBatch(sessionNow(), onSubmitMany, payloads, (i) => i);
  };

  /** Send one company again as a deliberate re-order. */
  const retry = (index: number) => {
    const payload = payloadsRef.current[index];
    if (!onSubmitMany || !payload) {
      return Promise.resolve();
    }
    setRows((previous) => withOutcome(previous, index, { status: 'sending' }, null));
    return runBatch(
      sessionNow(),
      onSubmitMany,
      [{ ...payload, allow_duplicate: true }],
      () => index
    );
  };

  return {
    rows,
    /** A batch has been sent in this session; the steps are behind. */
    started: rows.length > 0,
    inFlight,
    start,
    retry,
  };
}

export type BatchSubmit = ReturnType<typeof useBatchSubmit>;
